// =============================================================================
// VandieZ agent-web -- My Loads (/my-loads)
// =============================================================================
// AG-12. "Agent My Loads" (FSD p65: "My Loads manages all statuses").
//
// This is the counterpart to PostLoadPage and the reason the backend
// needed `allow_self_scope`: an agent is normally a delegate acting on an
// owner's resources via `?as=<owner_id>`, but loads they post are their
// own. `mine=true` self-scopes; WITHOUT it the server returns the
// marketplace, which is `open_for_bidding` only — so an agent's load
// would disappear from this list the moment a driver's bid was accepted,
// which is precisely when they most need to see it.
//
// "manages ALL statuses" is the requirement, so this page must not
// filter to open loads.
//
// FSD p59 asks for tabs: All / Active / Completed / Cancelled /
// Postponed. They filter what has been FETCHED rather than re-querying
// per tab, because the server's `?status=` takes a single value and
// "Active" spans two -- two round trips per tab switch to express one
// OR. The page therefore pulls a larger page and says plainly when the
// server holds more than it is showing, which is honest in a way that a
// silently-truncated tab count would not be.
// =============================================================================

import { AlertCircle, Loader2, Package, Plus } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { loadsApi } from "@/api/client";
import { AppHeader } from "@/components/AppHeader";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { ApiError } from "@/types/auth";
import type { Load } from "@/types/load";

function statusLabel(status: string): string {
  return status.replace(/_/g, " ");
}

/**
 * FSD p59's five tabs over the backend's seven statuses.
 *
 * `expired` deliberately belongs to no named tab. It is not active, was
 * not completed, and nobody cancelled or postponed it -- calling it any
 * of those would misreport what happened. It stays reachable under All.
 */
const TABS = [
  { key: "all", label: "All", match: null },
  {
    key: "active",
    label: "Active",
    match: ["draft", "open_for_bidding", "accepted"],
  },
  { key: "completed", label: "Completed", match: ["completed"] },
  { key: "cancelled", label: "Cancelled", match: ["cancelled"] },
  { key: "postponed", label: "Postponed", match: ["postponed"] },
] as const;

type TabKey = (typeof TABS)[number]["key"];

function formatDate(iso: string | null | undefined): string {
  if (iso === null || iso === undefined) {return "—";}
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-IN");
}

export function MyLoadsPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const [loads, setLoads] = useState<Load[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [tab, setTab] = useState<TabKey>("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (accessToken === null) {return;}
      setLoading(true);
      setError(null);
      try {
        const page = await loadsApi.listMine(
          accessToken,
          { limit: 200 },
          signal,
        );
        setLoads(page.items);
        setTotal(page.total);
      } catch (err) {
        if (signal?.aborted === true) {return;}
        setError(
          err instanceof ApiError ? err.message : "Could not load your loads.",
        );
      } finally {
        if (signal?.aborted !== true) {setLoading(false);}
      }
    },
    [accessToken],
  );

  useEffect(() => {
    const c = new AbortController();
    void load(c.signal);
    return () => {
      c.abort();
    };
  }, [load]);

  const active = TABS.find((t) => t.key === tab) ?? TABS[0];
  const visible =
    active.match === null
      ? loads
      : loads.filter((l) => (active.match as readonly string[]).includes(l.status));
  const countFor = (t: (typeof TABS)[number]): number =>
    t.match === null
      ? loads.length
      : loads.filter((l) => (t.match as readonly string[]).includes(l.status))
          .length;

  if (loading && loads.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        {/* No owner switcher: these are the agent's OWN loads, not
            delegated work. Showing "Acting as agent for X" here
            would state the opposite of what is happening. */}
        <AppHeader title="My loads" showOwnerSwitcher={false} />
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AppHeader title="My loads" showOwnerSwitcher={false} />
      <main className="mx-auto max-w-4xl space-y-4 p-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Loads you posted, in every status.
            {total > loads.length
              ? ` Showing the ${String(loads.length)} most recent of ${String(total)}.`
              : ""}
          </p>
          <Button asChild>
            <Link to="/post-load">
              <Plus className="mr-1 h-4 w-4" /> Post a load
            </Link>
          </Button>
        </div>

        <div className="flex flex-wrap gap-2" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => { setTab(t.key); }}
              className={
                tab === t.key
                  ? "rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground"
                  : "rounded-full bg-muted px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted/80"
              }
            >
              {t.label} ({countFor(t)})
            </button>
          ))}
        </div>

        {error !== null ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {loads.length === 0 && error === null ? (
          <Card>
            <CardContent className="flex flex-col items-center gap-3 py-12">
              <Package className="h-8 w-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                You have not posted any loads yet.
              </p>
              <Button asChild variant="outline">
                <Link to="/post-load">Post your first load</Link>
              </Button>
            </CardContent>
          </Card>
        ) : null}

        {loads.length > 0 && visible.length === 0 && error === null ? (
          <Card>
            <CardContent className="py-8 text-center text-sm text-muted-foreground">
              No {active.label.toLowerCase()} loads.
            </CardContent>
          </Card>
        ) : null}

        {visible.map((l) => (
          <Card key={l.id}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <CardTitle className="text-base">
                    {l.pickup_district} → {l.drop_district}
                  </CardTitle>
                  <CardDescription>
                    {l.material_description} · {l.weight_kg} kg ·{" "}
                    {formatDate(l.created_at)}
                  </CardDescription>
                </div>
                <span className="whitespace-nowrap rounded-full bg-muted px-2.5 py-1 text-xs font-medium capitalize">
                  {statusLabel(l.status)}
                </span>
              </div>
              {/* A postponed load shows the same new date as any other
                  load. Without the reason the agent cannot tell their
                  customer why it moved, which is the first thing they
                  will be asked. */}
              {l.status === "postponed" &&
              l.postponed_reason !== null &&
              l.postponed_reason !== undefined ? (
                <p className="pt-2 text-xs text-muted-foreground">
                  Postponed: {l.postponed_reason}
                </p>
              ) : null}
            </CardHeader>
          </Card>
        ))}
      </main>
    </div>
  );
}
